import { useState, useMemo, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { userAccountApi, useGetUserAccountsQuery, useCreateUserAccountMutation } from '../../../../../api/endpoints/userAccount';
import { useSetUserModuleRolesMutation } from '../../../../../api/endpoints/permissionManagement';
import { useLanguage } from '../../../../../contexts/LanguageContext';
import { resolveLegacyUsageType } from '../../constants';

const DEFAULT_PAGE_SIZE = 10;
const EMPTY_FILTERS = { firstName: '', lastName: '', email: '', dealerName: '' };

const mapSortOrderToDir = (order) => (order === 1 ? 'ASC' : order === -1 ? 'DESC' : null);

export const useUserAccessData = () => {
    const dispatch = useDispatch();
    const { getCurrentLanguageId } = useLanguage();
    const lang = Number(getCurrentLanguageId?.());
    const languageId = lang === 1 || lang === 2 ? lang : 1;

    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [sort, setSort] = useState({ field: null, order: null });
    const [filters, setFilters] = useState(EMPTY_FILTERS);

    const { data, isFetching, error, refetch } = useGetUserAccountsQuery({
        pageNumber: page,
        pageSize,
        languageId,
        ...filters,
        activeOnly: null,
        sortBy: sort.field,
        sortDir: mapSortOrderToDir(sort.order)
    });

    const [createUserAccount, { isLoading: creatingAccount }] = useCreateUserAccountMutation();
    const [setUserModuleRoles, { isLoading: savingRoles }] = useSetUserModuleRolesMutation();

    const users = useMemo(() => data?.data?.items || [], [data]);
    const totalCount = data?.data?.totalCount || 0;

    const lazyParams = useMemo(() => ({ first: (page - 1) * pageSize, rows: pageSize, sortField: sort.field, sortOrder: sort.order }), [page, pageSize, sort]);

    const onPage = (event) => {
        if (event.rows !== pageSize) {
            setPageSize(event.rows);
            setPage(1);
            return;
        }
        setPage(event.page + 1);
    };

    const onSort = (event) => setSort({ field: event.sortField, order: event.sortOrder });

    const applyFilters = (next) => {
        setFilters({ ...EMPTY_FILTERS, ...Object.fromEntries(Object.entries(next).map(([k, v]) => [k, (v || '').trim()])) });
        setPage(1);
    };

    // Hesap kaydı bool döner; Identity kullanıcı id'si e-posta ile listeden bulunur
    const findUserIdByEmail = useCallback(
        async (email) => {
            const result = await dispatch(userAccountApi.endpoints.getUserAccounts.initiate({ pageNumber: 1, pageSize: 20, languageId, email, activeOnly: null }, { forceRefetch: true })).unwrap();
            const match = (result?.data?.items || []).find((u) => (u.email || '').toLocaleLowerCase('tr') === email.toLocaleLowerCase('tr'));
            return match?.userId || null;
        },
        [dispatch, languageId]
    );

    const saveRoles = useCallback(({ userId, userType, roleIds }) => setUserModuleRoles({ userId, userType, roleIds }).unwrap(), [setUserModuleRoles]);

    /**
     * Yeni kullanıcı: 1) DMS hesap kaydı (Identity kullanıcısı + eski kullanım tipi rolü)
     * 2) Identity kullanıcı id'si bulunur 3) modül rolleri atanır.
     * 3. adım başarısız olursa hesap açılmış olur; hata { accountCreated: true } ile döner.
     */
    const createUser = useCallback(
        async ({ account, userType, moduleCodes, roleIds }) => {
            const email = account.email.trim();
            const response = await createUserAccount({
                firstName: account.firstName.trim(),
                lastName: account.lastName.trim(),
                email,
                password: account.password,
                passwordConfirmed: account.passwordConfirmed,
                role: '',
                dealerId: account.dealerId || 0,
                addrId: account.addrId || 0,
                usageType: resolveLegacyUsageType(userType, moduleCodes),
                identityNo: account.identityNo ? Number(account.identityNo) : 0,
                languageId: 2,
                isEmailEnabled: account.isEmailEnabled,
                country: account.country || '',
                phoneNumber: account.phoneNumber || ''
            }).unwrap();

            if (!response?.isSuccessful) throw new Error(response?.message);

            try {
                const userId = await findUserIdByEmail(email);
                if (!userId) throw new Error();
                if (roleIds.length > 0) await saveRoles({ userId, userType, roleIds });
                return { userId };
            } catch (error) {
                error.accountCreated = true;
                throw error;
            }
        },
        [createUserAccount, findUserIdByEmail, saveRoles]
    );

    return {
        users,
        totalCount,
        loading: isFetching,
        error,
        refetch,
        lazyParams,
        onPage,
        onSort,
        filters,
        applyFilters,
        resetFilters: () => applyFilters(EMPTY_FILTERS),
        createUser,
        saveRoles,
        saving: creatingAccount || savingRoles
    };
};
